import { Combobox as BaseCombobox } from "@base-ui/react/combobox"
import { Field } from "@base-ui/react/field"
import { css } from "dx-styles"
// oxlint-disable no-shadow
import { useMemo, type ReactElement } from "react"

import { bodyText, tokens } from "../theme"
import { controlSurface } from "./control"
import { fieldLabel, FieldMessage } from "./Field"
import { popupItem, popupPositioner, popupSurface } from "./popup"

const root = css({
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.xs,
})

const controlWrap = css({
  position: "relative",
  display: "flex",
})

const control = css(controlSurface, {
  // Room for the clear button and the chevron.
  paddingInlineEnd: tokens.size.md,
})

const endButton = css({
  position: "absolute",
  insetBlockStart: "50%",
  translate: "0 -50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  padding: tokens.space.none,
  color: tokens.color.textDescription,
  backgroundColor: "transparent",
  border: "none",
  borderRadius: tokens.radius.full,
  cursor: "pointer",
  "&:not(:disabled):hover": {
    backgroundColor: tokens.color.hover,
    color: tokens.color.text,
  },
  "&:focus-visible": {
    outline: `2px solid ${tokens.color.primary}`,
    outlineOffset: "1px",
  },
})

const clearButton = css({
  insetInlineEnd: tokens.size.xs,
})

const triggerButton = css({
  insetInlineEnd: tokens.space.xs,
})

const emptyState = css(bodyText, {
  color: tokens.color.textDescription,
  paddingBlock: tokens.space.xs,
  paddingInline: tokens.space.sm,
})

const itemIndicator = css({
  display: "inline-flex",
  flexShrink: 0,
  color: tokens.color.primary,
})

const ChevronIcon = () => {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <path
        d="M1 3.5L5 7.5L9 3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const ClearIcon = () => {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <path
        d="M1 1l8 8M9 1l-8 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

const CheckIcon = () => {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <path
        d="M1 5.5L3.5 8L9 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Values a combobox can carry. Narrow on purpose — these serialise predictably. */
export type ComboboxValue = string | number

/** One option. `label` is a string because it is what the query matches against. */
export type ComboboxItem<TValue extends ComboboxValue> = {
  value: TValue
  label: string
  disabled?: boolean
}

export type ComboboxProps<TValue extends ComboboxValue> = {
  /** Required — an unlabelled control is an accessibility failure. */
  label: string
  /** Hint shown under the control. Replaced by `error` when there is one. */
  description?: string
  /** When set, the field renders invalid and this replaces `description`. */
  error?: string
  /** Shown in the input until something is chosen. */
  placeholder?: string
  /** Shown in the popup when the query matches nothing. */
  emptyMessage?: string
  value?: TValue | null
  defaultValue?: TValue | null
  onValueChange?: (value: TValue | null) => void
  /** Adds a clear button. */
  clearable?: boolean
  disabled?: boolean
  required?: boolean
  /** Identifies the field when a form is submitted. */
  name?: string
  /** The options. `TValue` is inferred from these. */
  items: readonly ComboboxItem<TValue>[]
}

/**
 * A filtering select.
 *
 * Options come from `items` rather than compound children, because that is the
 * shape Base UI works in: it filters `Root`'s `items` and hands the survivors to
 * `List`'s render prop. A compound `<Combobox.Item>` API would have to read its
 * children, throw them away, and rebuild the rows from the filtered array — the
 * JSX would look like it rendered where it was written when it did not.
 *
 * `TValue` is inferred from `items`, so no factory is needed. (`Select` keeps its
 * factory because its children genuinely do render.)
 */
export const Combobox = <TValue extends ComboboxValue>({
  label: labelText,
  description,
  error,
  placeholder,
  emptyMessage = "No results",
  value,
  defaultValue,
  onValueChange,
  clearable = false,
  disabled,
  required,
  name,
  items,
}: ComboboxProps<TValue>): ReactElement => {
  /**
   * Base UI is given a flat array of raw values rather than the `{ value, label }`
   * objects, so that `value`/`onValueChange` deal in `TValue` rather than in
   * option objects. The labels are supplied separately via `itemToStringLabel`,
   * which is also what the filter matches on.
   */
  const { values, labels, disabledValues } = useMemo(() => {
    const values: TValue[] = []
    const labels = new Map<TValue, string>()
    const disabledValues = new Set<TValue>()

    for (const option of items) {
      values.push(option.value)
      labels.set(option.value, option.label)
      if (option.disabled) {
        disabledValues.add(option.value)
      }
    }

    return { values, labels, disabledValues }
  }, [items])

  return (
    <Field.Root
      className={root}
      invalid={Boolean(error)}
      disabled={disabled}
    >
      <Field.Label className={fieldLabel}>{labelText}</Field.Label>

      {/*
       * Parameterised with `TValue | null` on purpose: Base UI infers `Value`
       * from `items`, and its controlled `value` prop then rejects null — which
       * would leave a controlled combobox unable to express "nothing selected".
       */}
      <BaseCombobox.Root<TValue | null>
        items={values}
        itemToStringLabel={(itemValue) =>
          itemValue === null ? "" : (labels.get(itemValue) ?? String(itemValue))
        }
        value={value}
        defaultValue={defaultValue}
        disabled={disabled}
        required={required}
        name={name}
        onValueChange={(next) => onValueChange?.(next)}
      >
        <div className={controlWrap}>
          <BaseCombobox.Input
            className={control}
            placeholder={placeholder}
          />

          {clearable && (
            <BaseCombobox.Clear
              className={`${endButton} ${clearButton}`}
              aria-label={`Clear ${labelText}`}
            >
              <ClearIcon />
            </BaseCombobox.Clear>
          )}

          <BaseCombobox.Trigger
            className={`${endButton} ${triggerButton}`}
            aria-label={`Open ${labelText}`}
          >
            <ChevronIcon />
          </BaseCombobox.Trigger>
        </div>

        <BaseCombobox.Portal>
          <BaseCombobox.Positioner
            className={popupPositioner}
            sideOffset={4}
          >
            <BaseCombobox.Popup className={popupSurface}>
              <BaseCombobox.Empty className={emptyState}>
                {emptyMessage}
              </BaseCombobox.Empty>
              <BaseCombobox.List>
                {(itemValue: TValue) => (
                  <BaseCombobox.Item
                    key={String(itemValue)}
                    className={popupItem}
                    value={itemValue}
                    disabled={disabledValues.has(itemValue)}
                  >
                    {labels.get(itemValue) ?? String(itemValue)}
                    <BaseCombobox.ItemIndicator className={itemIndicator}>
                      <CheckIcon />
                    </BaseCombobox.ItemIndicator>
                  </BaseCombobox.Item>
                )}
              </BaseCombobox.List>
            </BaseCombobox.Popup>
          </BaseCombobox.Positioner>
        </BaseCombobox.Portal>
      </BaseCombobox.Root>

      <FieldMessage
        description={description}
        error={error}
      />
    </Field.Root>
  )
}
